import React, { useState, useRef, useCallback, useMemo, useEffect } from "react";
import { Button, Modal, notification, Spin } from "antd";
import { Elements } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import clientApi from "../../store/api";
import paymentMethodImg from "../../../../shared/AddPaymentMethod/paymentmethodimg.webp";
import ClientPaymentForm from "./ClientPaymentForm";
import SecuredCardIcon from "../../../../shared/AddPaymentMethod/cardIcon.webp";

const stripePromise = loadStripe(window.REACT_APP_STRIPE_PK);

const ClientPaymentModal = ({ open, onClose, clientId, onSuccess }) => {
  const [clientSecret, setClientSecret] = useState("");
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const paymentFormRef = useRef();

  const handleExternalFormSubmit = useCallback(() => {
    if (paymentFormRef.current) {
      paymentFormRef.current.requestSubmit();
    }
  }, []);

  const fetchClientSecret = useCallback(async () => {
    if (!clientId) return;

    try {
      setIsLoading(true);
      const res = await clientApi.createClientPaymentIntent(clientId);
      setClientSecret(res.clientSecret);
    } catch (error) {
      console.error("Error creating client payment intent:", error);
      notification.error({
        message:
          "Unable to create payment form for client. Please try again later or contact support."
      });
      onClose();
    } finally {
      setIsLoading(false);
    }
  }, [clientId, onClose]);

  // Auto-fetch client secret when modal opens
  useEffect(() => {
    if (open && clientId) {
      fetchClientSecret();
    }
  }, [open, clientId, fetchClientSecret]);

  const modalFooter = useMemo(
    () => (
      <div
        style={{
          display: "flex",
          justifyContent: "flex-end",
          alignItems: "center",
          padding: "10px"
        }}
      >
        <Button
          type="primary"
          onClick={handleExternalFormSubmit}
          disabled={formSubmitted || !clientSecret}
          loading={isLoading}
        >
          Add Payment Method
        </Button>
      </div>
    ),
    [handleExternalFormSubmit, formSubmitted, clientSecret, isLoading]
  );

  const handleModalCancel = useCallback(() => {
    onClose();
    setClientSecret("");
    setFormSubmitted(false);
    setIsLoading(false);
  }, [onClose]);

  return (
    <Modal
      open={open}
      onCancel={handleModalCancel}
      footer={modalFooter}
      width={700}
      centered
      title={`Add Payment Method for Client`}
    >
      <div
        style={{
          padding: "30px",
          display: "flex",
          flexWrap: "wrap",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "300px"
        }}
      >
        <div style={{ textAlign: "center", width: "100%" }}>
          <img
            src={SecuredCardIcon}
            alt="Payment Icon"
            style={{ width: "30px", height: "auto", marginRight: "10px" }}
          />
          <h2
            style={{
              width: "100%",
              textAlign: "center",
              marginBottom: "20px",
              fontSize: "20px",
              fontWeight: "bold",
              color: "#1F3998"
            }}
          >
            Payment Information
          </h2>
        </div>

        <Spin size="large" spinning={isLoading}>
          <Elements stripe={stripePromise}>
            {clientSecret ? (
              <ClientPaymentForm
                clientSecret={clientSecret}
                paymentFormRef={paymentFormRef}
                clientId={clientId}
                onClose={onClose}
                setIsLoading={setIsLoading}
                setFormSubmitted={setFormSubmitted}
                onSuccess={onSuccess}
              />
            ) : (
              <p>Loading payment form...</p>
            )}
          </Elements>
        </Spin>
      </div>
    </Modal>
  );
};

export default ClientPaymentModal;
